import { setCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        alignRight: boolean; 
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

/* ---------------------------------------------
   WRAPPER: CENTER OR RIGHT BASED ON FIELD
---------------------------------------------- */
const PaginationWrapper = styled.div<{ alignRight: boolean }>`
    display: flex;
    width: 100%;
    margin: 20px 0 25px 0;

    justify-content: ${({ alignRight }) =>
        alignRight ? "flex-end" : "center"};

    @media (max-width: 768px) {
        justify-content: center;
        margin: 15px 0 20px 0;
    }
`;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
    paginationCustom?: InjectableCss;
}

/* ---------------------------------------------
   INNER PAGINATION LAYOUT
---------------------------------------------- */
export const paginationStyles: ProductListPaginationStyles = {
    paginationCustom: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 12px;

            @media screen and (max-width: 768px) {
                flex-direction: column;
                gap: 10px;
            }
        `,
    },
};

const styles = paginationStyles;

const ProductListPagination: FC<Props> = ({ addProductFilters, pagination, fields }) => {
    if (!pagination || !pagination.totalItemCount) return null;

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });

        setTimeout(() => {
            if (typeof window !== "undefined") {
                window.scrollTo({
                    top: window.innerWidth < 768 ? 150 : 0,
                    behavior: "auto",
                });
            }
        }, 100);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
        setCookie("ProductList-PageSize", newPageSize.toString());
    };

    return (
        <PaginationWrapper alignRight={fields.alignRight}>
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="ProductList-PageSize"
                {...styles.paginationCustom}
            />
        </PaginationWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: [ProductListPageContext],
        fieldDefinitions: [
            {
                name: "alignRight",
                displayName: "Align Right",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;